import React, { useEffect } from 'react';
import { CheckCircleFill, XCircleFill, ExclamationCircleFill, InfoCircleFill } from 'react-bootstrap-icons';
import './AlertToast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface AlertToastProps {
  /** The message to display in the toast */
  message: string;
  /** The type of toast - determines color and icon. Default: 'success' */
  type?: ToastType;
  /** Callback function when the toast is closed */
  onClose: () => void;
  /** Whether to automatically close the toast. Default: true */
  autoClose?: boolean;
  /** Time in milliseconds before auto-closing. Default: 5000 (5 seconds) */
  autoCloseDelay?: number;
}

/**
 * AlertToast - A reusable toast notification component that appears in the lower right
 *
 * @example
 * ```tsx
 * const [showToast, setShowToast] = useState(true);
 *
 * return (
 *   <>
 *     {showToast && (
 *       <AlertToast
 *         message="Your profile has been updated."
 *         type="success"
 *         onClose={() => setShowToast(false)}
 *       />
 *     )}
 *   </>
 * );
 * ```
 */
const AlertToast: React.FC<AlertToastProps> = ({
  message,
  type = 'success',
  onClose,
  autoClose = true,
  autoCloseDelay = 5000,
}) => {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDelay, onClose]);

  const getToastTitle = () => {
    switch (type) {
      case 'success':
        return 'SUCCESS!';
      case 'error':
        return 'ERROR!';
      case 'warning':
        return 'WARNING!';
      case 'info':
      default:
        return 'INFO!';
    }
  };

  const getToastIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleFill />;
      case 'error':
        return <XCircleFill />;
      case 'warning':
        return <ExclamationCircleFill />;
      case 'info':
      default:
        return <InfoCircleFill />;
    }
  };

  const toastClass = `alert-toast ${type}-toast`;

  return (
    <div className={toastClass}>
      <div className="toast-icon-section">
        <div className="toast-icon-circle">
          {getToastIcon()}
        </div>
      </div>
      <div className="toast-content">
        <div className="toast-title">{getToastTitle()}</div>
        <div className="toast-message">{message}</div>
      </div>
      <button className="toast-close" onClick={onClose} aria-label="Close notification">
        ×
      </button>
    </div>
  );
};

export default AlertToast;
